import { FETCH_ERRORS as e } from 'modules/messagingInsights/constants';
import { useCampaignAndMetricFiltersContext } from 'modules/messagingInsights/context';
import { useRestData } from 'modules/messagingInsights/hooks/useRestData';
import {
  selectCampaignMetricsTableData,
  selectCampaignsLoading,
  selectError,
} from 'modules/messagingInsights/selectors';
import { useMetricsContext } from 'modules/productAnalytics/context';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { NoResult } from 'ui/illustrations';
import { Campaign, RestDataIdentifier, TimeSeriesData } from '../../types';
import { getCampaignMediumsByFrequency } from '../../utils';
import { NoDataMessage } from '../NoDataMessage';
import { TimeSeriesChart } from './TimeSeriesChart';
import {
  getLoadingData,
  getMediumSpecificChartData,
  getXMediumChartData,
  sortBySelectionOrder,
} from './utils';

export const CrossMediumTimeSeries = () => {
  // All selected metrics, ordered by selection order
  const { selectedCrossMetrics, MediumFilteredAMs, MediumFilteredHLMs } =
    useCampaignAndMetricFiltersContext();
  // Metrics fetched from graphql
  const { analyticsMetrics } = useMetricsContext();

  // List of medium-specific + X-medium metrics (used to populate loading charts)
  const mediumSpecificMetrics = selectedCrossMetrics.filter((metric) =>
    MediumFilteredAMs.has(metric),
  );
  const XMediumMetrics = selectedCrossMetrics.filter((metric) => MediumFilteredHLMs.has(metric));

  // Data processing
  const {
    data: timeSeriesData,
    loading: timeSeriesLoading,
    error: timeSeriesFetchError,
  } = useRestData(RestDataIdentifier.TIME_SERIES);

  const campaignMetricsTableData: Campaign[] = useSelector(selectCampaignMetricsTableData);
  const campaignsLoading = useSelector(selectCampaignsLoading);
  const errors = useSelector(selectError) || {};
  const campaignsFetchError = Object.keys(errors).includes(e.FETCH_CAMPAIGNS);

  const numCampaignsPerMedium = useMemo(() => {
    if (!campaignMetricsTableData) return {};
    return getCampaignMediumsByFrequency(campaignMetricsTableData as Campaign[]);
  }, [campaignMetricsTableData]);

  const mediumSpecificChartData = timeSeriesLoading
    ? getLoadingData(mediumSpecificMetrics)
    : getMediumSpecificChartData(timeSeriesData as TimeSeriesData, analyticsMetrics);

  const XMediumChartData = timeSeriesLoading
    ? getLoadingData(XMediumMetrics)
    : getXMediumChartData(timeSeriesData as TimeSeriesData);

  const allChartData = sortBySelectionOrder(
    selectedCrossMetrics,
    XMediumChartData.concat(mediumSpecificChartData),
  );

  const allCharts = allChartData.map((chartData) => (
    <TimeSeriesChart
      key={chartData.metric}
      chartData={chartData}
      numCampaignsPerMedium={numCampaignsPerMedium}
    />
  ));

  const showErrorState = timeSeriesFetchError || campaignsFetchError;
  const showLoadingState = timeSeriesLoading || campaignsLoading;

  return (
    <div>
      {!showErrorState && allCharts}
      {!showLoadingState && showErrorState && (
        <NoDataMessage
          Icon={NoResult}
          title="Sorry we couldn't load this page"
          subtitle="Try refreshing the page. If this keeps happening, let us know."
        />
      )}
    </div>
  );
};
